import { useMemo, type ReactNode } from "react";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { COPY } from "../../copy";
import type { P8ContinuedSalesPoint } from "../../p8OverviewData";
import { monthIndexFromLabel } from "../../p8OverviewData";

import {
  ChartHeadline,
  ChartHoverPanel,
  ChartLegend,
  ChartMonthAxisTick,
  ChartNavButtons,
  ChartTooltipExplore,
  ChartTooltipRows,
  CHART_LEGEND_GAP_CLASS,
  CHART_MARGIN_WITH_AXIS_LABELS,
  CHART_Y_AXIS_WIDTH,
  chartXAxisLabel,
  findNiceStepDollars,
  formatChartAxisCurrency,
  useChartHoverPanel,
  type ChartLegendItem,
  type ChartTooltipRow,
} from "./chartShared";

const IMMEDIATE_COLOR = "#2d2a70";
const CONTINUED_ACTUAL_COLOR = "#AFC1FF";
const REMAINDER_FILL = "#ecebf7";
const REMAINDER_STROKE = "#47477C";
const SPEND_COLOR = "#F26532";
const REMAINDER_LABEL = COPY.overview.charts.forecast.remainderLabel;
const FIRST_YEAR_LABEL = COPY.overview.charts.forecast.firstYearLabel;

/**
 * Miniature of the three-segment bar: immediate, continued actual, then the
 * dashed remainder on top. Stands in for a single fill on the first-year total,
 * which is the full height of the bar rather than one of its segments.
 */
function StackedBarSwatch() {
  return (
    <span
      className="relative h-2.5 w-2.5 shrink-0 overflow-hidden rounded-[2px]"
      aria-hidden
    >
      <span
        className="absolute inset-x-0 top-0 h-[4px] border border-dashed"
        style={{
          backgroundColor: REMAINDER_FILL,
          borderColor: REMAINDER_STROKE,
        }}
      />
      <span
        className="absolute inset-x-0 top-[4px] h-[3px]"
        style={{ backgroundColor: CONTINUED_ACTUAL_COLOR }}
      />
      <span
        className="absolute inset-x-0 bottom-0 h-[3px]"
        style={{ backgroundColor: IMMEDIATE_COLOR }}
      />
    </span>
  );
}

const Y_AXIS_TICK_COUNT = 5;
const BAR_TOP_RADIUS = 4;

type ForecastRemainderSalesAndSpendChartProps = {
  data: P8ContinuedSalesPoint[];
  onOpenMonth?: (monthIndex: number) => void;
  /** Rendered in the header, left of the month-paging arrows. */
  headerAction?: ReactNode;
};

const LEGEND_ITEMS: ChartLegendItem[] = [
  {
    key: "firstOrderSales",
    label: "Immediate ad sales",
    color: IMMEDIATE_COLOR,
    tooltip: COPY.overview.charts.forecast.immediate,
  },
  {
    key: "continuedSales",
    label: "Continued ad sales (actual)",
    color: CONTINUED_ACTUAL_COLOR,
    tooltip: COPY.overview.charts.forecast.continued,
  },
  {
    key: "projectedFirstYearSales",
    label: REMAINDER_LABEL,
    color: REMAINDER_FILL,
    outline: true,
    ringColor: REMAINDER_STROKE,
    tooltip: COPY.overview.charts.forecast.remainder,
  },
  {
    key: "adSpend",
    label: "Ad spend",
    color: SPEND_COLOR,
    tooltip: COPY.overview.charts.forecast.spend,
  },
];

/**
 * The three segments are mutually exclusive, so the cap belongs to the highest
 * one that has any value: the remainder for a maturing cohort, the actuals for a
 * cohort that has already reached 12 months.
 */
function getTopSegmentKey(point: P8ContinuedSalesPoint): string {
  if (point.projectedFirstYearSales > 0) {
    return "projectedFirstYearSales";
  }
  if (point.continuedSales > 0) {
    return "continuedSales";
  }
  return "firstOrderSales";
}

type BarShapeProps = {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  fill?: string;
  stroke?: string;
  strokeDasharray?: string | number;
  payload?: P8ContinuedSalesPoint;
};

function createRoundedTopBar(dataKey: string) {
  return function RoundedTopBar({
    x = 0,
    y = 0,
    width = 0,
    height = 0,
    fill,
    stroke,
    strokeDasharray,
    payload,
  }: BarShapeProps) {
    if (width <= 0 || height <= 0) {
      return null;
    }
    const isTop = payload ? getTopSegmentKey(payload) === dataKey : false;
    const r = isTop ? Math.min(BAR_TOP_RADIUS, width / 2, height) : 0;
    const d = r
      ? `M${x},${y + height} L${x},${y + r} Q${x},${y} ${x + r},${y} L${
          x + width - r
        },${y} Q${x + width},${y} ${x + width},${y + r} L${x + width},${
          y + height
        } Z`
      : `M${x},${y} L${x + width},${y} L${x + width},${y + height} L${x},${
          y + height
        } Z`;
    return (
      <path
        d={d}
        fill={fill}
        stroke={stroke}
        strokeDasharray={strokeDasharray}
      />
    );
  };
}

function computeYAxisLayout(data: P8ContinuedSalesPoint[]) {
  const dataMax = Math.max(
    0,
    ...data.map((point) =>
      Math.max(
        point.firstOrderSales +
          point.continuedSales +
          point.projectedFirstYearSales,
        point.adSpend
      )
    )
  );
  const target = Math.max(dataMax, 1000);
  const step = findNiceStepDollars(target / (Y_AXIS_TICK_COUNT - 1));
  const top = step * (Y_AXIS_TICK_COUNT - 1);
  const ticks = Array.from(
    { length: Y_AXIS_TICK_COUNT },
    (_, index) => index * step
  );
  return { domain: [0, top] as [number, number], ticks };
}

type SquareDotProps = { cx?: number; cy?: number; fill?: string };

function SquarePlotDot({ cx = 0, cy = 0, fill = "#333" }: SquareDotProps) {
  return (
    <rect
      x={cx - 3}
      y={cy - 3}
      width={6}
      height={6}
      rx={1}
      ry={1}
      fill={fill}
    />
  );
}

function ChartTooltip({
  label,
  point,
  onExplore,
}: {
  label: string;
  point: P8ContinuedSalesPoint;
  /** Absent when the chart has nowhere to drill into. */
  onExplore?: () => void;
}) {
  // Flat, unlike the combined-forecast chart: here the actual and the remainder
  // are separate slices of the first-year total rather than one inside the
  // other, so nothing is indented under anything else. The first-year total
  // comes after the three segments it sums, reading bottom-up like the stack it
  // describes. Ad spend joins them above the rule rather than below it — it is
  // as measured as they are, and the rule now separates the figures from the
  // action.
  const rows: ChartTooltipRow[] = [
    {
      key: "firstOrderSales",
      label: "Immediate ad sales",
      value: point.firstOrderSales,
      depth: 0,
      swatch: { backgroundColor: IMMEDIATE_COLOR },
    },
    {
      key: "continuedSales",
      label: "Continued ad sales (actual)",
      value: point.continuedSales,
      depth: 0,
      swatch: { backgroundColor: CONTINUED_ACTUAL_COLOR },
    },
    {
      key: "projectedFirstYearSales",
      label: REMAINDER_LABEL,
      value: point.projectedFirstYearSales,
      depth: 0,
      swatch: {
        backgroundColor: REMAINDER_FILL,
        border: `1px dashed ${REMAINDER_STROKE}`,
      },
    },
    {
      key: "firstYearTotal",
      label: FIRST_YEAR_LABEL,
      value:
        point.firstOrderSales +
        point.continuedSales +
        point.projectedFirstYearSales,
      depth: 0,
      swatchNode: <StackedBarSwatch />,
    },
    {
      key: "adSpend",
      label: "Ad spend",
      value: point.adSpend,
      depth: 0,
      swatch: { backgroundColor: SPEND_COLOR },
    },
  ];

  return (
    <div className="bg-fs-surface-primary rounded-md border border-[#dfe0e1] px-3 py-2 text-[14px] leading-[20px] text-[#333] shadow-[0_2px_8px_rgba(0,0,0,0.15)]">
      <div className="mb-1 font-medium">{label}</div>
      <ChartTooltipRows rows={rows} />
      {onExplore ? <ChartTooltipExplore onClick={onExplore} /> : null}
    </div>
  );
}

/**
 * Same first-year total as the combined-forecast chart, split differently: the
 * forecast segment is only the part still to come, so the dashed band reads as
 * "left to earn" rather than as an envelope wrapped around the actuals.
 */
export function ForecastRemainderSalesAndSpendChart({
  data,
  onOpenMonth,
  headerAction,
}: ForecastRemainderSalesAndSpendChartProps) {
  const { domain, ticks } = useMemo(() => computeYAxisLayout(data), [data]);
  const {
    hover,
    boundsRef,
    onChartMouseMove,
    onChartMouseLeave,
    onPanelEnter,
    onPanelLeave,
  } = useChartHoverPanel();
  const hoveredPoint = hover
    ? data.find((point) => point.month === hover.label)
    : undefined;

  return (
    <div className="bg-fs-surface-primary flex h-full min-h-0 w-full flex-col rounded-lg border border-[#dfe0e1] p-6">
      <ChartHeadline
        title={COPY.overview.charts.salesAndSpendTitle}
        description={COPY.overview.charts.forecast.description}
        actions={
          <div className="flex items-center gap-3">
            {headerAction}
            <ChartNavButtons />
          </div>
        }
      />

      <div
        className={`mt-4 flex min-h-0 flex-1 flex-col ${CHART_LEGEND_GAP_CLASS}`}
      >
        {/* Relative, so the hover panel can be positioned against the plot it
            describes while staying outside the Recharts wrapper. */}
        <div className="relative min-h-0 flex-1" ref={boundsRef}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={data}
              margin={CHART_MARGIN_WITH_AXIS_LABELS}
              barCategoryGap="30%"
              barSize={12}
              onMouseMove={onChartMouseMove}
              onMouseLeave={onChartMouseLeave}
            >
              <CartesianGrid
                stroke="#DFE0E1"
                strokeDasharray="3 0"
                vertical={false}
              />
              <XAxis
                dataKey="month"
                tickLine={{ stroke: "#DFE0E1" }}
                axisLine={{ stroke: "#DFE0E1" }}
                interval={0}
                height={44}
                tick={(props) => <ChartMonthAxisTick {...props} />}
                label={chartXAxisLabel("Month")}
              />
              <YAxis
                domain={domain}
                ticks={ticks}
                interval={0}
                tick={{ fontSize: 11, fill: "#757575" }}
                tickLine={false}
                axisLine={false}
                tickFormatter={(value: number) =>
                  formatChartAxisCurrency(value)
                }
                width={CHART_Y_AXIS_WIDTH}
              />
              {/* Kept only for the column highlight, which Recharts draws from
                  this element. The panel itself is rendered below, outside the
                  chart, so it can hold a button. */}
              <Tooltip
                shared
                cursor={{ fill: "rgba(0, 0, 0, 0.04)" }}
                content={() => null}
              />
              <Bar
                dataKey="firstOrderSales"
                stackId="sales"
                fill={IMMEDIATE_COLOR}
                shape={createRoundedTopBar("firstOrderSales")}
                isAnimationActive={false}
              />
              <Bar
                dataKey="continuedSales"
                stackId="sales"
                fill={CONTINUED_ACTUAL_COLOR}
                shape={createRoundedTopBar("continuedSales")}
                isAnimationActive={false}
              />
              <Bar
                dataKey="projectedFirstYearSales"
                stackId="sales"
                fill={REMAINDER_FILL}
                stroke={REMAINDER_STROKE}
                strokeDasharray="2 2"
                shape={createRoundedTopBar("projectedFirstYearSales")}
                isAnimationActive={false}
              />
              <Line
                type="linear"
                dataKey="adSpend"
                stroke={SPEND_COLOR}
                strokeWidth={1.5}
                dot={(props: SquareDotProps) => (
                  <SquarePlotDot {...props} fill={SPEND_COLOR} />
                )}
                activeDot={(props: SquareDotProps) => (
                  <SquarePlotDot {...props} fill={SPEND_COLOR} />
                )}
                isAnimationActive={false}
              />
            </ComposedChart>
          </ResponsiveContainer>

          {hover && hoveredPoint ? (
            <ChartHoverPanel
              hover={hover}
              onMouseEnter={onPanelEnter}
              onMouseLeave={onPanelLeave}
            >
              <ChartTooltip
                label={hover.label}
                point={hoveredPoint}
                onExplore={
                  onOpenMonth
                    ? () => onOpenMonth(monthIndexFromLabel(hover.label))
                    : undefined
                }
              />
            </ChartHoverPanel>
          ) : null}
        </div>

        <ChartLegend items={LEGEND_ITEMS} />
      </div>
    </div>
  );
}
